// Ügyfél-detail „Számlázási profilok" szekció (`?clientId={id}`).
//
// KORÁBBAN read-only volt, SZÁNDÉKOSAN belépő nélkül („NINCS új profil hozzáadása") — és a `ClientDetailSchema`
// beágyazott `billingProfiles` tömbjéből renderelt. Most a saját lista-végpontjáról kérdez (mint a másik három
// szekció), és VAN „hozzáadás" belépője: egy ügyfél-oldalon a számlázási profil felvétele ugyanolyan természetes
// művelet, mint a fiók vagy a kártya — az önkényes kivétel csak arra kényszerítené a felhasználót, hogy elhagyja
// az ügyfelet, megkeresse a Számlázási profilok lista-oldalt, és ott válassza ki újra ugyanezt az ügyfelet.
//
// Három slot (`RelatedList`): **számlázási név · cím · „elsődleges" jel**.
import { type BillingProfileRowSchema } from '@atlas/contracts';
import {
  ClipboardDocumentIcon,
  IdentificationIcon,
  PencilSquareIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type z } from 'zod';

import { BillingProfileFormSheet } from '@/components/billing-profiles/billing-profile-form-sheet';
import { DomainBadge } from '@/components/shared/domain-badge';
import { FormSection } from '@/components/shared/form-section';
import { RelatedList, type RelatedListItem } from '@/components/shared/related-list';
import { Button } from '@/components/ui/button';
import { billingProfileKeys } from '@/features/billing-profiles/api';
import { copyBillingDetails } from '@/features/billing-profiles/billing-details-text';
import { CLIENT_SECTION } from '@/features/clients/sections';
import { api } from '@/lib/api';
import { isForbiddenStatus } from '@/lib/error-status';
import { usePermissions } from '@/lib/use-permissions';

type BillingProfileRow = z.infer<typeof BillingProfileRowSchema>;

/**
 * Az ügyfél számlázási profiljai — KÖZÖS lekérés a szekció ÉS az „automatikus számlázás" kapcsoló között.
 *
 * MIÉRT KÖZÖS: a kapcsoló azon múlik, van-e ELSŐDLEGES profil. Ha a detail-oldal a saját, beágyazott
 * `detail.billingProfiles` tömbjéből döntene, akkor egy ITT (a szekcióban) most felvett elsődleges profil után
 * a kapcsoló ZÁROLVA MARADNA, amíg a felhasználó újra nem tölti az oldalt — a profil-írás ugyanis a
 * `billingProfileKeys.lists()` kulcsot invalidálja, nem az ügyfél-detailt. Egy forrás → a kapcsoló magától
 * felold. (A React Query a kulcs alapján deduplikál, tehát ez NEM plusz hálózati kérés.)
 */
export function useClientBillingProfiles(clientId: string) {
  // `isPrimary` DESC, majd név — az elsődleges profil elöl (a lista-oldal rendezésével egyező).
  return api.billingProfiles.useQuery([...billingProfileKeys.lists(), 'by-client', clientId], {
    query: { clientId, sort: 'billingName', order: 'asc', limit: 200 },
  });
}

export function ClientBillingProfilesSection({
  clientId,
  clientName,
}: {
  clientId: string;
  clientName: string;
}): JSX.Element {
  const { t } = useTranslation();
  const { can } = usePermissions();
  const canManage = can('billing_profiles.manage');

  const [createOpen, setCreateOpen] = useState(false);
  const [editRow, setEditRow] = useState<BillingProfileRow | null>(null);

  const query = useClientBillingProfiles(clientId);
  const rows: BillingProfileRow[] = query.data?.status === 200 ? query.data.body.profiles : [];
  const errStatus = query.error?.status;

  const items: RelatedListItem[] = rows.map((p) => ({
    id: p.id,
    title: p.billingName ?? '—',
    meta: [[p.zipCode, p.city].filter(Boolean).join(' '), p.address].filter(Boolean).join(', '),
    status: p.isPrimary ? (
      <DomainBadge kind="marker" value="primary" label={t('clients.primaryBadge')} />
    ) : undefined,
    // MÁSOLÁS mindenkinek (olvasási művelet: a számlázási adatok egyben, pl. levélbe); a ceruza a kezelési joggal.
    // Az akció-slot miatt a sor maga nem kattintható — a szerkesztés ezért a ceruzán át megy.
    actions: (
      <span className="flex items-center gap-0.5">
        <button
          type="button"
          className="text-muted-foreground hover:text-foreground focus-visible:ring-ring rounded p-1 focus-visible:outline-none focus-visible:ring-2"
          onClick={() => copyBillingDetails(p, t)}
          aria-label={t('billingProfiles.copyDetails.actionFor', { name: p.billingName ?? '—' })}
        >
          <ClipboardDocumentIcon className="h-4 w-4" />
        </button>
        {canManage && (
          <button
            type="button"
            className="text-muted-foreground hover:text-foreground focus-visible:ring-ring rounded p-1 focus-visible:outline-none focus-visible:ring-2"
            onClick={() => setEditRow(p)}
            aria-label={t('billingProfiles.copyDetails.editFor', { name: p.billingName ?? '—' })}
          >
            <PencilSquareIcon className="h-4 w-4" />
          </button>
        )}
      </span>
    ),
  }));

  return (
    <FormSection id={CLIENT_SECTION.billingProfiles} title={t('clients.billingProfilesTitle')}>
      <RelatedList
        items={items}
        isLoading={query.isLoading}
        isError={query.isError}
        forbidden={isForbiddenStatus(errStatus)}
        onRetry={() => void query.refetch()}
        // NINCS kattintható belépő: a modul lista-oldalán ma nincs ügyfél-szűrő, tehát a „megtekintés" a
        // teljes, szűretlen listára dobna — az rosszabb, mint a puszta darabszám. (Ha a lista-oldal kap
        // ügyfél-szűrőt, ide is bekerül az `onShowAll`, mint a Szolgáltatásoknál.)
        showAllLabel={(total) => t('clients.relatedTotal', { count: total })}
        emptyLabel={t('clients.billingProfilesEmpty')}
        emptyIcon={IdentificationIcon}
        errorLabel={t('clients.relatedError')}
        forbiddenLabel={t('clients.relatedForbidden')}
        retryLabel={t('clients.relatedRetry')}
      />

      {/* A „hozzáadás" belépő a szekció TARTALMÁNAK VÉGÉN, BALRA (user-döntés, 2026-08-18) — ugyanaz a
          gomb-alak, mint a profil „Jelszó módosítása" / „2FA beállítása" gombja. Korábban a fejléc jobb
          szélén ült: ott vizuálisan nehezebb volt a halk `overline` címnél, és négy szekciónál egy egész
          gomb-oszlop uralta a lapot. A tartalom végén viszont ott van, ahol a felhasználó a lista átnézése
          UTÁN jár. Nincs `mt-*` — a térközt a `FormSection` `space-y-4`-e adja. */}
      {canManage && (
        <div className="flex">
          <Button type="button" variant="outline" onClick={() => setCreateOpen(true)}>
            <PlusIcon />
            {t('clients.addBillingProfile')}
          </Button>
        </div>
      )}

      <BillingProfileFormSheet
        open={createOpen}
        onOpenChange={setCreateOpen}
        profile={null}
        lockedClient={{ id: clientId, name: clientName }}
      />
      <BillingProfileFormSheet
        open={editRow !== null}
        onOpenChange={(o) => !o && setEditRow(null)}
        profile={editRow}
      />
    </FormSection>
  );
}
